import { Component, OnInit, ViewEncapsulation, Input, SimpleChanges, OnChanges, OnDestroy, Output, EventEmitter, SecurityContext } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { fadeInOut } from '../../shared/helpers/animations';
import { StorageManagerService } from '../../shared/services/storage-manager.service';
import { IContactDetail, IEntity, ILLBusinessCategory } from '../../shared/models/index';
import { CompanyContactService, AuthorizationService, ContactDialogService } from '../../shared/services/index';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { Subscription } from 'rxjs';
import { Utilities } from '../../shared/services/utilities';
import { phoneNumberValidator } from '../../shared/helpers/phone-number.validator';
import { LCRFormComponent, ERFormComponent } from '../index';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-contact-detail',
    templateUrl: './contact-detail.component.html',
    styleUrls: ['./contact-detail.component.scss'],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ContactDetailComponent implements OnInit, OnChanges, OnDestroy {
    @Input() contactRevisionId: string;
    @Input() contactData: IContactDetail;
    @Input() companyType: string;

    @Input() parentComponentInput: LCRFormComponent | ERFormComponent;

    @Output() cancelButtonClickedOutput = new EventEmitter<any>();
    @Output() contactOutput = new EventEmitter<any>();
    @Output() contactRetrievedOutput = new EventEmitter<any>();


    sub: Subscription;
    public contact: IContactDetail;
    public contactForm: UntypedFormGroup;
    public pageTitle: string;
    countries: IEntity[];
    regions: IEntity[];
    businessCategories: ILLBusinessCategory[];
    // Use with the generic validation message class
    displayMessage: { [key: string]: string } = {};
    errorMessage: string;
    selectedBusinessCategoryId: string;
    selectedCountryId: string;
    selectedRegionId: string;
    regionFromDrp: string;
    buttonText: string;
    contactId: string;
    submitted = false;
    bFirstTime = true;
    loading = false;
    isFormDraftFlag = false;
    version: string;
    formStatus: string;
    COUNTRY_USA = '185'; // Country code for United States
    countryRegion: any;
    country: any;
    countryId: any;
    businessCategoryId: any;
    bFromCompanyPage: boolean;
    bFromCompanyPageEditLink = false;
    bFromContactValidationFlow = false;
    bSavedContact: boolean;
    bIsDraftMode: boolean;
    bHasPermission = false;
    bHasDeletePermission = false;

    // Company Types
    COMPANY_TYPE_EST = 'Establishment_Owned';
    COMPANY_TYPE_IMPORTER = 'Importer';
    COMPANY_TYPE_LABELER = 'Labeler';
    COMPANY_TYPE_REGISTRANT = 'Registrant';
    COMPANY_TYPE_US_AGENT = 'US_Agent';
    // maps the appropriate column to fields property
    public businessCategoryFields: Object = {
        text: 'label',
        value: 'categoryId'
    };
    public countryFields: Object = {
        text: 'label',
        value: 'id'
    };
    public stateFields: Object = {
        text: 'regionAndAbbreviation',
        value: 'regionAndAbbreviation'
    };

    // set the placeholder to DropDownList input elements
    public businessCategoryWaterMark = 'Select a Business Category';
    public stateWaterMark = 'Select a State/Region';
    public countryWaterMark = 'Select a Country';

    formErrors = {
        email: '',
        firstName: '',
        lastName: '',
        isActive: '',
        address: '',
        city: '',
        countryRegion: '',
        zip: '',
        countryId: '',
        businessCategoryId: '',
        status: '',
        phoneExt: '',
        areaCode: '',
        dialCode: '',
        phoneNumber: ''
    };

    constructor (
        private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router, private authorizService: AuthorizationService,
        private authService: AuthenticationService,
        private toastr: ToastrService, private storageManagerService: StorageManagerService,
        private utilService: Utilities,
        private contactService: CompanyContactService, 
        private sanitizer: DomSanitizer,
        private contactDialogService: ContactDialogService) { }

    // set the height of the popup element
    public ddwHeight = '220px';
    public ddwWdith = '240px';

    ngOnInit(): void {
        this.contactForm = this.fb.group({
            firstName: ['', { validators: [Validators.required, Validators.pattern(/^[^0-9]*$/)], updateOn: 'blur' }],
            lastName: ['', { validators: [Validators.required, Validators.pattern(/^[^0-9]*$/)], updateOn: 'blur' }],
            email: ['', { validators: [Validators.required, this.emailValidator], updateOn: 'blur' }],
            phoneNumber: ['', { validators: [Validators.required], updateOn: 'blur' }],
            address: ['', { updateOn: 'blur' }],
            city: ['', { updateOn: 'blur' }],
            countryId: ['', { updateOn: 'blur' }],
            countryRegion: ['', { updateOn: 'blur' }],
            zip: ['', { updateOn: 'blur' }],
            businessCategoryId: [''],
            phoneExt: ['', { validators: [Validators.pattern(/^[0-9]*$/)], updateOn: 'blur' }],
            dialCode: ['', { validators: [Validators.required, Validators.pattern(/^\+?[0-9-]*$/)], updateOn: 'blur' }],
            areaCode: ['', { validators: [Validators.required], updateOn: 'blur' }],
            status: ['Active', { validators: [Validators.required], updateOn: 'blur' }],
            saveAsDraft: ['']
        }, { validator: phoneNumberValidator('dialCode', 'areaCode', 'phoneNumber') });

        this.applyConditionalAddressValidators();

        // Check if opened from company page dialog
        const isFromCompanyPage = sessionStorage['fromCompanyPage'] === 'true';
        this.bFromContactValidationFlow = sessionStorage['fromContactValidationFlow'] === 'true';
        
        // Try to get contact data from the service first (most reliable for dialog usage)
        const serviceContactData = this.contactDialogService.getCurrentContactData();
        const serviceContactRevisionId = this.contactDialogService.getCurrentContactRevisionId();
        const serviceCompanyType = this.contactDialogService.getCurrentCompanyType();
        
        if (isFromCompanyPage && serviceContactData) {
            // Initialize from the service data (preferred method)
            this.initializeFromServiceData(serviceContactData, serviceContactRevisionId, serviceCompanyType);
        } else if (isFromCompanyPage && this.contactData) {
            // Fallback: Initialize from the @Input contactData
            this.initializeFromInputData();
        } else {
            // Initialize for standalone usage
            this.initParams();

            if (this.contactData) {
                this.initializeStandaloneContactData(this.contactData);
            }

            if (!this.contactRevisionId) {
                // Set contact status default to "Active"
                this.getFormControlByName('status').patchValue('Active');
                this.isFormDraftFlag = false;
                this.version = '';
                this.formStatus = '';
                this.selectedBusinessCategoryId = '';
                this.selectedCountryId = '';
                this.selectedRegionId = '';
            }
        }

        // set focus on the invisible div so the first tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
        this.initTabIndex();
    }

    /**
     * Initialize the form from service data (used when opened from company page dialog)
     */
    private initializeFromServiceData(contactData: IContactDetail, contactRevisionId: string, companyType: string): void {
        this.bFromCompanyPage = true;
        this.bFromCompanyPageEditLink = true;
        this.contact = contactData;
        this.contactData = contactData;
        this.contactId = contactData.contactId;
        this.contactRevisionId = contactRevisionId || sessionStorage['currentContactRevisionId'];
        this.companyType = companyType;
        this.applyConditionalAddressValidators();
        this.bIsDraftMode = contactData.isDraftMode;
        this.pageTitle = contactData.contactName;
        this.buttonText = 'Update contact';
        this.errorMessage = '';
        
        // Initialize dropdown data
        this.getBusinessCategory();
        this.getCountries();
        this.checkUserPermission();
        this.utilService.toggleClientDropdownState(true);
        
        // Populate the form with contact data
        this.displayContact(contactData);
    }

    /**
     * Initialize the form from @Input contactData (fallback method)
     */
    private initializeFromInputData(): void {
        this.bFromCompanyPage = true;
        this.bFromCompanyPageEditLink = true;
        this.contact = this.contactData;
        this.contactId = this.contactData.contactId;
        this.contactRevisionId = sessionStorage['currentContactRevisionId'];
        this.bIsDraftMode = this.contactData.isDraftMode;
        this.pageTitle = this.contactData.contactName;
        this.buttonText = 'Update contact';
        this.errorMessage = '';
        
        // Initialize dropdown data
        this.getBusinessCategory();
        this.getCountries();
        this.checkUserPermission();
        this.utilService.toggleClientDropdownState(true);
        
        // Populate the form with contact data
        this.displayContact(this.contactData);
    }

    /**
     * Initialize the standalone contact page from the parent ContactComponent data.
     */
    private initializeStandaloneContactData(contactData: IContactDetail): void {
        this.bFromCompanyPage = false;
        this.bFromCompanyPageEditLink = false;
        this.contact = contactData;
        this.contactId = contactData.contactId;
        this.contactRevisionId = contactData.contactRevisionId || this.contactRevisionId || this.activatedRoute.snapshot.params.contactRevisionId;
        this.bIsDraftMode = contactData.isDraftMode;
        this.pageTitle = contactData.contactName;
        this.buttonText = 'Update contact';
        this.errorMessage = '';

        this.displayContact(contactData);
        this.contactRetrievedOutput.emit(this.contact);
    }

    initTabIndex() {
      (document.activeElement as HTMLElement).blur();

      const main = document.getElementById('firstDiv');

      if (main) {
        main.focus();
      }
    }

    // convenience getter for easy access to form fields
    get f() { return this.contactForm.controls; }

    // Check if user has write permission. If no, disable the "Update Contact" button
    checkUserPermission() {
        if (this.authorizService.hasPermission('SSDP.CanWriteContact')) {
            this.bHasPermission = true;
        }
        if (this.authorizService.hasPermission('SSDP.CanDeleteContact')) {
            this.bHasDeletePermission = true;
        }
    }

    // Get detail information for a specific contact
    getContactById(): void {

        if (this.contactRevisionId) {
            this.contactService.getContactById(this.contactRevisionId).subscribe(data => {
                if (data) {
                    this.countries = data['countries'];
                    this.regions = data['regions'];
                    this.contact = data['contactDetail'];
                    this.contactId = this.contact.contactId;
                    this.bIsDraftMode = this.contact.isDraftMode;
                    this.displayContact(this.contact);

                    this.contactRetrievedOutput.emit(this.contact);

                }
            },
              error => {
                this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
              }
            );
        }

    }

    // Retrieve list of all the business categoy
    getBusinessCategory(): void {
        this.contactService.getBusinessCategories(false).subscribe(data => {
            if (data) {
                this.businessCategories = data;
            }
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
    }

    // Build an ILLBusinessCategory object for Business Category
    getGenericKeyObjectForBC(list: any[], id: any): ILLBusinessCategory {
        const retVal = new ILLBusinessCategory();

        if (list && id) {
            list = list.filter(x => x['categoryId'] === id);
            if (list && list.length > 0) {
                retVal.categoryId = id && id !== '0' ? id : '';
                retVal.label = list[0]['label'];
            }
        }
        return retVal;
    }

    // Retrieve list of countries, default country to "United States"
    getCountries(): void {
        // Only get contactRevisionId from route if not already set (for standalone usage)
        if (!this.contactRevisionId) {
            this.contactRevisionId = this.activatedRoute.snapshot.params.contactRevisionId;
        }
        this.contactService.getCountries().subscribe(data => {
            if (data) {
                this.countries = data['countryList'];
            }
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
    }

    // Retrieve a list of Regions/States based on the country selected by the user
    getRegionsByCountry(countryCode: string): void {
        this.contactService.getRegionsByCountry(countryCode).subscribe(data => {
            if (data) {
                this.regions = data;
                // Pre select the drop down to the user's countryRegion
                this.selectedRegionId = this.contact && this.contact.countryRegion ? this.contact.countryRegion : '';
            }
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
    }

    // Get the state/regions for the country selected
    countrySelectionChanged(event) {
        if (event.itemData) {
            this.getRegionsByCountry(event.itemData.id);
            this.contactForm.controls['countryRegion'].setValue('');
            if (!this.bFirstTime) {
                this.regionFromDrp = '';
                this.selectedRegionId = '';
            }
        } else {// selection being removed
            this.regions = [];
        }
        this.bFirstTime = false;
    }

    stateSelectionChanged(event) {
        this.regionFromDrp = '';
        if (event.itemData) {
            this.regionFromDrp = event.itemData.regionAndAbbreviation;
        }
    }

    // Populate the form
    displayContact(contact: IContactDetail): void {
        this.contactForm.reset();
        this.setcontactForm(contact);
    }

    public clearForm() {
        this.contactForm.reset();
    }

    // if the region code is not valid, set to empty
    validateRegion(regionCode: string) {
        if (this.regions) {
            const match = this.regions.filter(x => x.regionAndAbbreviation === regionCode);
            return match.length > 0 ? regionCode : '';
        } else {
            return '';
        }
    }

    // Save/Update contact info to the database
  saveContact(formValues): void {
    this.submitted = true;
    this.loading = true;
    this.errorMessage = '';
    const sessionCurrentCustomer = this.storageManagerService.read('currentCustomer');
    const currentCustomer = typeof sessionCurrentCustomer === 'object' ? sessionCurrentCustomer : JSON.parse(sessionCurrentCustomer);
    const authData = this.authService.getAuthData();

        // stop here if form is invalid
        if (this.contactForm.invalid) {
            this.loading = false;
            return;
        }

        if (this.contactForm.valid) {

            this.contactForm.markAsPristine();
            this.prepareDataForSave(formValues);

            this.contactService.createContact(formValues)
                .subscribe(data => {

                    this.loading = false;
                    this.bSavedContact = true;

                    if (!this.bFromCompanyPage) {
                        if (!this.bIsDraftMode) {
                            if (!this.parentComponentInput) {
                                this.router.navigate(['/contact-dashboard']);
                                return data;
                            } else {

                                if (this.parentComponentInput && (this.parentComponentInput instanceof LCRFormComponent || this.parentComponentInput instanceof ERFormComponent)) {
                                    this.contactOutput.emit(data);
                                }
                            }
                        } else {
                            // Force angular to rerun ngOnInit to reload data when navigating to address
                            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                            this.router.onSameUrlNavigation = 'reload';
                            this.router.navigate(['/contact', data]);
                        }
                    } else {
                        sessionStorage['ValidatedContactSave'] = 'true';
                        this.toastr.success('Contact saved successfully.');
                        // Update the version number on the form
                        this.getLatestContactVersion();
                    }
                    return data;
                },
                    error => {
                        this.loading = false;
                        this.toastr.error(error && error.errorMessage ? error.errorMessage : error);

                    }
                );

        }
    }

    // set the form as draft mode
    setDraftMode() {
        this.bIsDraftMode = true;
    }

    // set the form as active mode
    setActiveMode() {
        this.bIsDraftMode = false;
    }

    // Populate the contact form
    setcontactForm(contact: IContactDetail) {
        if (!contact) {
            return;
        }
        
        this.contactForm.patchValue({
            // firstName: this.sanitizedValue(contact.firstName),
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phoneNumber: contact.phoneNumber,
            address: contact.address,
            city: contact.city,
            zip: contact.zip,
            phoneExt: contact.phoneExtension,
            dialCode: contact.dialCode,
            areaCode: contact.areaCode,
            status: contact.loAFlag === true ? 'Inactive' : 'Active'
        });

        // Use the passed contact parameter instead of this.contact to avoid null reference
        this.selectedCountryId = +contact.countryId === 0 || contact.countryId === null || contact.countryId === undefined ? '' : contact.countryId;
        this.selectedRegionId = contact.countryRegion ? contact.countryRegion : '';
        this.selectedBusinessCategoryId = contact.businessCategory && contact.businessCategory.categoryId ? contact.businessCategory.categoryId : null;
        this.contactForm.patchValue({
            businessCategoryId: this.selectedBusinessCategoryId
        });

        this.getFormControlByName('countryId').setValue(this.selectedCountryId);
        this.getFormControlByName('countryRegion').setValue(this.selectedRegionId);
        this.regionFromDrp = this.selectedRegionId;
        this.isFormDraftFlag = contact.formActiveFlag === 'Active' ? false : true;
        this.version = contact.version;
        this.formStatus = 'Form status: ' + contact.formActiveFlag;
        
        // Fetch regions if country is selected
        if (this.selectedCountryId) {
            this.getRegionsByCountry(this.selectedCountryId);
        }
  }

  sanitizedValue(value): SafeHtml {

    return this.sanitizer.sanitize(SecurityContext.HTML, value);
  }

    // Get the object ready before being saved
    prepareDataForSave(formValues) {
        const sessionCurrentCustomer = this.storageManagerService.read('currentCustomer');
        const currentCustomer = typeof sessionCurrentCustomer === 'object' ? sessionCurrentCustomer : JSON.parse(sessionCurrentCustomer);
        const authData = this.authService.getAuthData();

        formValues['modifiedBy'] = authData.name;
        formValues['loAFlag'] = formValues['status'] === 'Inactive' ? true : false;
        formValues['IsDraftMode'] = this.bIsDraftMode === true ? true : false;
        // formValues['businessCategoryId'] = this.f.businessCategoryId.value === '' ? this.selectedBusinessCategoryId : this.f.businessCategoryId.value;
        // formValues['businessCategory'] = formValues['businessCategoryId'];
        formValues['businessCategory'] = this.getGenericKeyObjectForBC(this.businessCategories, this.f.businessCategoryId.value === '' ? this.selectedBusinessCategoryId : this.f.businessCategoryId.value);

        formValues['countryRegion'] = this.f.countryRegion.value === '' ? this.selectedRegionId : this.f.countryRegion.value;
        formValues['countryRegion'] = this.validateRegion(formValues['countryRegion']);
        formValues['countryId'] = this.f.countryId.value === '' ? this.selectedCountryId : this.f.countryId.value;
        formValues['country'] = formValues['countryId'];
        formValues['userId'] = currentCustomer.customerGuid;
        // if we are creating a new contact, we need to clear out the contactId
        if (this.contactRevisionId === null || this.contactRevisionId === undefined || this.contactRevisionId === '') {
            formValues['contactRevisionId'] = null;
            sessionStorage['currentContactId'] = null;
            formValues['parentVersion'] = '0.0';
        } else {
            formValues['contactRevisionId'] = this.contactRevisionId;
            formValues['contactId'] = this.contactId;
            formValues['parentVersion'] = this.contact.version;
        }
    }


    cancelBtnClicked() {

        if (!this.parentComponentInput) {

            this.router.navigateByUrl('/contact-dashboard');
        } else {
            this.contactForm.clearValidators();
            this.contactForm.reset();
            this.cancelButtonClickedOutput.emit();
        }
    }

    // Get a form control by name
    getFormControlByName(controlName: string): UntypedFormControl {
        return <UntypedFormControl>this.contactForm.get(controlName);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Skip if form is not initialized yet (ngOnInit hasn't run)
        if (!this.contactForm) {
            return;
        }

        if (changes['companyType']) {
            this.applyConditionalAddressValidators();
        }

        this.bFromCompanyPageEditLink = sessionStorage['fromCompanyPage'] === 'true';
        this.bFromContactValidationFlow = sessionStorage['fromContactValidationFlow'] === 'true';
        this.bFromCompanyPage = this.bFromCompanyPageEditLink;
        this.bSavedContact = false;

        // Handle contactData changes - this is how data is passed from both the company dialog and the standalone contact page
        if (changes['contactData'] && changes['contactData'].currentValue) {
            const contactDataValue = changes['contactData'].currentValue as IContactDetail;

            if (!this.bFromCompanyPageEditLink) {
                this.initializeStandaloneContactData(contactDataValue);
                return;
            }

            this.contact = contactDataValue;
            this.contactId = contactDataValue.contactId;
            this.bIsDraftMode = contactDataValue.isDraftMode;
            this.pageTitle = contactDataValue.contactName;
            this.buttonText = 'Update contact';
            this.contactRevisionId = sessionStorage['currentContactRevisionId'];
            this.errorMessage = '';
            
            // Initialize dropdown data
            this.getBusinessCategory();
            this.getCountries();
            this.checkUserPermission();
            this.utilService.toggleClientDropdownState(true);
            
            // Populate the form with contact data
            this.displayContact(contactDataValue);
            return;
        }

        // Handle contactRevisionId changes (for cases where component fetches its own data)
        // This is for when the component is used outside of the company page dialog
        if (changes['contactRevisionId'] && !this.bFromCompanyPageEditLink) {
            if (this.contactRevisionId != undefined && this.contactRevisionId !== '') {
                this.buttonText = 'Update contact';
                this.bFromCompanyPage = false;
                this.selectedBusinessCategoryId = '';
                this.selectedCountryId = '';
                this.selectedRegionId = '';
                this.regionFromDrp = '';
                this.errorMessage = '';
                this.getContactById();
            } else {
                this.getBusinessCategory();
                this.getCountries();
            }
        }
    }

  /**
   * Check if the company type requires address fields (StreetAddress, City, Country, PostalCode)
   * Only Registrant, Establishment_Owned, and Labeler require these fields.
   * Importer and US_Agent do NOT require address fields.
   */
  isAddressRequiredCompanyType(): boolean {
    if (!this.companyType) {
      return false;
    }
    return (this.companyType === this.COMPANY_TYPE_EST || 
            this.companyType === this.COMPANY_TYPE_LABELER || 
            this.companyType === this.COMPANY_TYPE_REGISTRANT);
  }

  /**
   * Apply conditional required validators to address and postal code fields based on company type
   */
  private applyConditionalAddressValidators(): void {
    this.getFormControlByName('address').clearValidators();
    this.getFormControlByName('city').clearValidators();
    this.getFormControlByName('countryId').clearValidators();
    this.getFormControlByName('zip').clearValidators();

    if (this.isAddressRequiredCompanyType()) {
      // Add Validators.required to address, city, countryId, and zip for company types that require them
      this.getFormControlByName('address').setValidators([Validators.required]);
      this.getFormControlByName('city').setValidators([Validators.required]);
      this.getFormControlByName('countryId').setValidators([Validators.required]);
      this.getFormControlByName('zip').setValidators([Validators.required]);
    }
    // Update validity to reflect the new validators
    this.getFormControlByName('address').updateValueAndValidity();
    this.getFormControlByName('city').updateValueAndValidity();
    this.getFormControlByName('countryId').updateValueAndValidity();
    this.getFormControlByName('zip').updateValueAndValidity();
  }

  isRequiredTypes(country: any): boolean {

    let retVal = false;

    if (this.companyType) {
      retVal = (this.companyType === this.COMPANY_TYPE_EST || this.companyType === this.COMPANY_TYPE_LABELER || this.companyType === this.COMPANY_TYPE_REGISTRANT) && country === +this.COUNTRY_USA;
    } else {
      retVal = country === +this.COUNTRY_USA;
    }
    return retVal;
  }

  // get the latest version number of the current contact
  getLatestContactVersion() {
      this.contactService.getContacts().subscribe(data => {
          if (data) {
              const currentContact = data['latestAvailableContactsList'].filter(x => x.contactId === +sessionStorage['ValidatedContactId']);
              if (currentContact && currentContact[0]) {
                  // this.version = currentContact[0].version.substring(0, currentContact[0].version.length - 1);
                  this.version = currentContact[0].version;
              }

          }
      },
        error => this.toastr.error(error.errorMessage)
      );
  }

    // Initialization for standalone usage (not from company page dialog)
    initParams() {
        this.contactForm.reset();
        this.getBusinessCategory();
        this.getCountries();
        this.errorMessage = '';
        this.bFromCompanyPage = false;
        this.bFromCompanyPageEditLink = false;
        this.utilService.toggleClientDropdownState(true);
        this.checkUserPermission();
    }

    /**
     * Function to duplicate the Validators.Email
     * @param  {AbstractControl} control - the control to validate
     */
    emailValidator(control) {
        if (control.value) {
            const matches = control.value.match(/[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?/);
            return matches ? null : { 'invalidEmail': true };
        } else {
            return null;
        }
    }

    onSaveComplete(): void {
        this.contactForm.reset();
        this.router.navigate(['/conpany-contact-list']);
    }

    ngOnDestroy(): void {
        sessionStorage['fromCompanyPage'] = false;
        this.utilService.toggleClientDropdownState(false);
    }
}


